import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Add, SwapVert, Delete, FiberManualRecord, Sync, GetApp } from '@mui/icons-material';
import { projectsService } from '../services/projects';
import { ProjectResponse } from '../types';
import { useLanguage } from '../contexts/LanguageContext';
import Navbar from '../components/Navbar';
import EmptyState from '../components/EmptyState';
import { SkeletonList } from '../components/SkeletonLoader';
import { parseGenelPuan } from '../utils/scoreParser';

type SortField = 'date' | 'score' | 'name';
type SortOrder = 'asc' | 'desc';

const DashboardPage: React.FC = () => {
  const [projects, setProjects] = useState<ProjectResponse[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState<SortField>('date');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  const [deletingProjectId, setDeletingProjectId] = useState<number | null>(null);
  const [downloadingProjectId, setDownloadingProjectId] = useState<number | null>(null);
  const [parsedScores, setParsedScores] = useState<Record<number, number>>({});
  const [loadingScores, setLoadingScores] = useState<Set<number>>(new Set());
  const navigate = useNavigate();
  const { t, language } = useLanguage();
  const isTr = language === 'tr';

  const loadProjects = useCallback(async () => {
    setLoading(true);
    try {
      const data = await projectsService.getProjects();
      setProjects(data.projects || []);
    } catch (error: any) {
      console.error('Failed to load projects:', error);
      setProjects([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProjects();
  }, [loadProjects]);

  const loadParsedScore = useCallback(async (projectId: number) => {
    if (loadingScores.has(projectId) || parsedScores[projectId] !== undefined) return;

    setLoadingScores(prev => new Set(prev).add(projectId));
    try {
      const timeoutPromise = new Promise((_, reject) =>
        setTimeout(() => reject(new Error('Request timeout')), 30000)
      );
      const analysisPromise = projectsService.getAnalysis(projectId);
      const analysis = await Promise.race([analysisPromise, timeoutPromise]) as any;

      if (analysis?.sections) {
        const puanlamaKey = Object.keys(analysis.sections).find(key =>
          key.includes('BÖLÜM 7') ||
          key.includes('Bölüm 7') ||
          key.includes('MR.MIRROR PUAN SİSTEMİ') ||
          key.includes('Mr.Mirror Puan Sistemi') ||
          key.includes('BÖLÜM 8') ||
          key.includes('Bölüm 8') ||
          key.includes('PUANLAMA') ||
          key.includes('Puanlama')
        );

        if (puanlamaKey) {
          const genelPuan = parseGenelPuan(analysis.sections[puanlamaKey]);
          if (genelPuan !== null && genelPuan > 0) {
            setParsedScores(prev => ({ ...prev, [projectId]: genelPuan }));
            return;
          }
        }
        if (analysis.full_analysis) {
          const genelPuan = parseGenelPuan(analysis.full_analysis);
          if (genelPuan !== null && genelPuan > 0) {
            setParsedScores(prev => ({ ...prev, [projectId]: genelPuan }));
          }
        }
      }
    } catch (error) {
      console.error(`Failed to load parsed score for project ${projectId}:`, error);
    } finally {
      setLoadingScores(prev => {
        const newSet = new Set(prev);
        newSet.delete(projectId);
        return newSet;
      });
    }
  }, [loadingScores, parsedScores]);

  useEffect(() => {
    if (projects.length === 0 || loading) return;
    const projectsNeedingScore = projects
      .filter(p => (!p.score || p.score === 0) && p.analysis_status === 'completed')
      .filter(p => !loadingScores.has(p.id) && parsedScores[p.id] === undefined)
      .slice(0, 5);
    if (projectsNeedingScore.length === 0) return;
    projectsNeedingScore.forEach((project, index) => {
      setTimeout(() => loadParsedScore(project.id), index * 1000);
    });
  }, [projects.length, loading, loadParsedScore, loadingScores, parsedScores]);

  const sortedAndFilteredProjects = useMemo(() => {
    let filtered = projects.filter(project =>
      project.file_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      project.project_type?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortField) {
        case 'date': {
          const parseDate = (dateStr: string | undefined): number => {
            if (!dateStr) return 0;
            try {
              const [datePart, timePart] = dateStr.split(' ');
              if (!datePart || !timePart) return 0;
              const isoDate = `${datePart}T${timePart}`;
              const date = new Date(isoDate);
              const ts = date.getTime();
              if (isNaN(ts)) {
                const fb = new Date(dateStr).getTime();
                return isNaN(fb) ? 0 : fb;
              }
              return ts;
            } catch {
              return 0;
            }
          };
          const dateA = parseDate(a.upload_date);
          const dateB = parseDate(b.upload_date);
          if (dateA === 0 && dateB === 0) comparison = 0;
          else if (dateA === 0) comparison = 1;
          else if (dateB === 0) comparison = -1;
          else comparison = dateA - dateB;
          break;
        }
        case 'score':
          comparison = (a.score || 0) - (b.score || 0);
          break;
        case 'name':
          comparison = (a.file_name || '').localeCompare(b.file_name || '', 'tr');
          break;
      }
      return sortOrder === 'asc' ? comparison : -comparison;
    });

    return filtered;
  }, [projects, searchQuery, sortField, sortOrder]);

  const handleSort = (field: SortField) => {
    if (sortField === field) setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('desc'); }
  };

  const handleDeleteProject = async (e: React.MouseEvent, projectId: number, projectName: string) => {
    e.stopPropagation();
    if (!window.confirm(t.deleteConfirm.replace('{name}', projectName))) return;
    setDeletingProjectId(projectId);
    try {
      await projectsService.deleteProject(projectId);
      await loadProjects();
    } catch (err: any) {
      alert(t.projectDeleteFailed + ': ' + (err?.response?.data?.detail || err?.message));
    } finally {
      setDeletingProjectId(null);
    }
  };

  const handleDownloadReport = async (e: React.MouseEvent, projectId: number, fileName?: string) => {
    e.stopPropagation();
    setDownloadingProjectId(projectId);
    try {
      const token = localStorage.getItem('access_token');
      const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1')
        .replace(/(\\[rn])+$/g, '')
        .replace(/[\r\n\t\s]+$/g, '')
        .replace(/^\s+/g, '');
      const url = token
        ? `${API_BASE_URL}/projects/${projectId}/analysis-pdf?token=${encodeURIComponent(token)}`
        : `${API_BASE_URL}/projects/${projectId}/analysis-pdf`;
      const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      if (response.ok) {
        const blob = await response.blob();
        if (blob.size === 0) { alert(t.downloadFailed + ': Boş dosya.'); return; }
        const downloadUrl = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = downloadUrl;
        a.download = `${(fileName || 'rapor').replace(/\.[^.]+$/, '')}_tam_analiz.pdf`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(downloadUrl);
        document.body.removeChild(a);
      } else {
        const errText = await response.text();
        alert(t.downloadFailed + ': ' + (errText || response.statusText));
      }
    } catch (err: any) {
      alert(t.downloadFailed + ': ' + (err?.message || ''));
    } finally {
      setDownloadingProjectId(null);
    }
  };

  const getSortLabel = (field: SortField) =>
    ({ date: t.sortByDate, score: t.sortByScore, name: t.sortByName }[field]);

  const formatDate = (d?: string) => {
    if (!d) return '—';
    try {
      const date = new Date(d.replace(' ', 'T'));
      return date.toLocaleDateString(isTr ? 'tr-TR' : 'en-US', {
        year: 'numeric', month: 'short', day: '2-digit',
      });
    } catch {
      return d;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <main className="max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-24">
          <div className="h-12 mb-12 bg-elevated animate-pulse rounded-none" />
          <div className="border border-hairline">
            <SkeletonList rows={5} />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <main className="max-w-7xl mx-auto px-6 md:px-10 pt-24 md:pt-32 pb-16 md:pb-24">
        {/* Section header */}
        <div className="flex items-baseline justify-between mb-12">
          <span className="eyebrow"><span className="text-accent">/01</span> · {isTr ? 'PANELİN' : 'YOUR PANEL'}</span>
          <span className="eyebrow-mute hidden md:inline">
            {sortedAndFilteredProjects.length} {t.projects.toUpperCase()}
          </span>
        </div>

        {/* Heading + new project CTA */}
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h1 className="display-h display-md">
                {isTr ? <>{t.myProjects} <em>·</em></> : <>{t.myProjects} <em>·</em></>}
              </h1>
              <button
                onClick={loadProjects}
                disabled={loading}
                className="text-ink-mute hover:text-accent transition-colors p-1"
                title={isTr ? 'Senkronize Et' : 'Sync'}
              >
                <Sync className={loading ? 'animate-spin' : ''} sx={{ fontSize: 18 }} />
              </button>
            </div>
            <p className="font-prose text-[15px] text-ink-dim">
              {isTr
                ? `${sortedAndFilteredProjects.length} proje · sıralı, aranabilir, kayıt altında.`
                : `${sortedAndFilteredProjects.length} projects · sorted, searchable, on the record.`}
            </p>
          </div>
          <button onClick={() => navigate('/upload')} className="btn-accent self-start md:self-auto">
            <Add sx={{ fontSize: 14 }} />
            {t.newProject.toUpperCase()}
          </button>
        </div>

        {/* Toolbar — search + sort */}
        <div className="flex flex-col md:flex-row md:items-center gap-3 mb-10 pb-6 border-b border-hairline">
          <div className="relative flex-1">
            <Search className="absolute left-0 top-1/2 -translate-y-1/2 text-ink-mute" sx={{ fontSize: 16 }} />
            <input
              type="text"
              placeholder={`${t.search}…`}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-7 pr-2 py-3 bg-transparent border-0 border-b border-hairline focus:border-accent focus:outline-none font-prose text-[15px] text-ink placeholder:text-ink-mute transition-colors"
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="eyebrow-mute mr-2">{t.sort.toUpperCase()}</span>
            {(['date', 'score', 'name'] as SortField[]).map((field) => (
              <button
                key={field}
                onClick={() => handleSort(field)}
                className={`flex items-center gap-1 px-3 py-2 font-mono text-[10px] tracking-[0.18em] uppercase font-semibold border transition-colors ${
                  sortField === field
                    ? 'border-accent text-accent bg-accent-soft'
                    : 'border-hairline text-ink-dim hover:border-accent hover:text-accent'
                }`}
              >
                <span>{getSortLabel(field)}</span>
                {sortField === field && (
                  <SwapVert
                    className={sortOrder === 'asc' ? 'rotate-180' : ''}
                    sx={{ fontSize: 12, transition: 'transform .2s ease' }}
                  />
                )}
              </button>
            ))}
          </div>
        </div>

        {/* Project list — editorial table */}
        <div className="hidden md:block">
          {/* Header row */}
          <div className="grid grid-cols-12 gap-4 px-2 py-3 border-b border-hairline">
            <div className="col-span-5 eyebrow-mute">{t.workName}</div>
            <div className="col-span-2 eyebrow-mute">{t.uploadDate}</div>
            <div className="col-span-2 eyebrow-mute text-right">{t.overallScore}</div>
            <div className="col-span-2 eyebrow-mute">{t.status}</div>
            <div className="col-span-1 eyebrow-mute text-right">{t.actions}</div>
          </div>

          {sortedAndFilteredProjects.map((project, i) => {
            const displayScore = parsedScores[project.id] ?? (project.score && project.score > 0 ? project.score : null);
            const isProcessing = project.analysis_status === 'processing';
            const isUnseen = project.analysis_status === 'completed' && project.analysis_viewed === 0;
            const typeLabel = project.project_type === 'Film Senaryosu' ? t.filmScript :
              project.project_type === 'Dizi Senaryosu' ? t.seriesScript :
              project.project_type;

            return (
              <div
                key={project.id}
                className="group grid grid-cols-12 gap-4 items-center px-2 py-5 border-b border-hairline hover:bg-accent-soft transition-colors"
              >
                <button
                  onClick={() => navigate(`/projects/${project.id}`)}
                  className="col-span-11 grid grid-cols-11 gap-4 items-center text-left"
                >
                  {/* Name + type */}
                  <div className="col-span-5 min-w-0">
                    <div className="flex items-center gap-2.5 mb-1.5">
                      <span className="font-mono text-[10px] tracking-wider text-ink-mute tabular-nums shrink-0">
                        {String(i + 1).padStart(2, '0')}
                      </span>
                      {isUnseen && (
                        <FiberManualRecord
                          sx={{ fontSize: 8 }}
                          className="text-accent animate-pulse shrink-0"
                        />
                      )}
                      {isProcessing && (
                        <div className="w-2 h-2 border border-accent border-t-transparent rounded-full animate-spin shrink-0" />
                      )}
                      <h3 className="font-sans text-[15px] font-medium leading-snug text-ink group-hover:text-accent transition-colors truncate">
                        {project.file_name}
                      </h3>
                    </div>
                    <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-ink-mute">
                      · {typeLabel}
                    </span>
                  </div>

                  {/* Date */}
                  <div className="col-span-2 font-mono text-[11px] tracking-wider uppercase text-ink-dim">
                    {formatDate(project.upload_date)}
                  </div>

                  {/* Score */}
                  <div className="col-span-2 flex items-baseline justify-end gap-1">
                    <span className="font-serif italic text-3xl text-accent leading-none tabular-nums">
                      {displayScore !== null ? displayScore.toFixed(1) : (loadingScores.has(project.id) ? '…' : '—')}
                    </span>
                    <span className="font-mono text-[10px] tracking-wider text-ink-mute">/10</span>
                  </div>

                  {/* Status */}
                  <div className="col-span-2">
                    {isProcessing ? (
                      <span className="inline-flex items-center gap-2 font-mono text-[10px] tracking-[0.18em] uppercase text-accent">
                        <div className="w-2 h-2 border border-accent border-t-transparent rounded-full animate-spin" />
                        {isTr ? 'İŞLENİYOR' : 'PROCESSING'}
                      </span>
                    ) : (
                      <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-success">
                        ✓ {t.analyzed.toUpperCase()}
                      </span>
                    )}
                  </div>
                </button>

                {/* Actions */}
                <div className="col-span-1 flex items-center justify-end gap-1">
                  {project.generate_report && project.analysis_status === 'completed' && (
                    <button
                      onClick={(e) => handleDownloadReport(e, project.id, project.file_name || undefined)}
                      disabled={downloadingProjectId === project.id}
                      className="w-8 h-8 flex items-center justify-center text-ink-dim hover:text-accent hover:border-accent border border-transparent transition-colors"
                      title={t.downloadReport}
                    >
                      {downloadingProjectId === project.id ? (
                        <div className="w-3 h-3 border border-accent border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <GetApp sx={{ fontSize: 16 }} />
                      )}
                    </button>
                  )}
                  <button
                    onClick={(e) => handleDeleteProject(e, project.id, project.file_name || 'Proje')}
                    disabled={deletingProjectId === project.id}
                    className="w-8 h-8 flex items-center justify-center text-ink-dim hover:text-signal-red hover:border-signal-red border border-transparent transition-colors"
                    title={isTr ? 'Projeyi Sil' : 'Delete project'}
                  >
                    <Delete sx={{ fontSize: 16 }} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Mobile: stacked card list */}
        <div className="md:hidden space-y-px bg-hairline">
          {sortedAndFilteredProjects.map((project, i) => {
            const displayScore = parsedScores[project.id] ?? (project.score && project.score > 0 ? project.score : null);
            const isProcessing = project.analysis_status === 'processing';
            const isUnseen = project.analysis_status === 'completed' && project.analysis_viewed === 0;
            const typeLabel = project.project_type === 'Film Senaryosu' ? t.filmScript :
              project.project_type === 'Dizi Senaryosu' ? t.seriesScript :
              project.project_type;

            return (
              <div key={project.id} className="bg-background p-5">
                <button onClick={() => navigate(`/projects/${project.id}`)} className="w-full text-left">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 mb-1.5">
                        <span className="font-mono text-[10px] tracking-wider text-ink-mute">
                          {String(i + 1).padStart(2, '0')}
                        </span>
                        {isUnseen && <FiberManualRecord sx={{ fontSize: 8 }} className="text-accent animate-pulse" />}
                        {isProcessing && <div className="w-2 h-2 border border-accent border-t-transparent rounded-full animate-spin" />}
                      </div>
                      <h3 className="font-sans text-[15px] font-medium text-ink leading-snug mb-1">
                        {project.file_name}
                      </h3>
                      <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-ink-mute">
                        {typeLabel}
                      </span>
                    </div>
                    <div className="flex flex-col items-end shrink-0">
                      <span className="font-serif italic text-3xl text-accent leading-none tabular-nums">
                        {displayScore !== null ? displayScore.toFixed(1) : (loadingScores.has(project.id) ? '…' : '—')}
                      </span>
                      <span className="font-mono text-[9px] tracking-wider text-ink-mute mt-1">/10</span>
                    </div>
                  </div>
                  <div className="flex items-center justify-between mt-4 pt-3 border-t border-hairline">
                    <span className="font-mono text-[10px] tracking-wider uppercase text-ink-dim">
                      {formatDate(project.upload_date)}
                    </span>
                    {isProcessing ? (
                      <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-accent">
                        {isTr ? 'İŞLENİYOR' : 'PROCESSING'}
                      </span>
                    ) : (
                      <span className="font-mono text-[10px] tracking-[0.18em] uppercase text-success">
                        ✓ {t.analyzed.toUpperCase()}
                      </span>
                    )}
                  </div>
                </button>
                <div className="flex items-center justify-end gap-1 mt-3">
                  {project.generate_report && project.analysis_status === 'completed' && (
                    <button
                      onClick={(e) => handleDownloadReport(e, project.id, project.file_name || undefined)}
                      disabled={downloadingProjectId === project.id}
                      className="w-8 h-8 flex items-center justify-center text-ink-dim hover:text-accent transition-colors border border-hairline"
                      aria-label={t.downloadReport}
                    >
                      {downloadingProjectId === project.id ? (
                        <div className="w-3 h-3 border border-accent border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <GetApp sx={{ fontSize: 16 }} />
                      )}
                    </button>
                  )}
                  <button
                    onClick={(e) => handleDeleteProject(e, project.id, project.file_name || 'Proje')}
                    disabled={deletingProjectId === project.id}
                    className="w-8 h-8 flex items-center justify-center text-ink-dim hover:text-signal-red transition-colors border border-hairline"
                    aria-label={isTr ? 'Projeyi Sil' : 'Delete project'}
                  >
                    <Delete sx={{ fontSize: 16 }} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {sortedAndFilteredProjects.length === 0 && (
          <div className="py-24 text-center max-w-md mx-auto">
            <span className="eyebrow mb-4 block">/ {isTr ? 'BOŞ' : 'EMPTY'}</span>
            <h3 className="display-h display-md mb-4">
              {searchQuery
                ? (isTr ? <>Bu aramada <em>sonuç yok.</em></> : <>No <em>matches.</em></>)
                : (isTr ? <>Henüz <em>hiçbir şey</em> yok.</> : <>Nothing <em>yet.</em></>)}
            </h3>
            <p className="font-prose text-[15px] text-ink-dim mb-8">
              {searchQuery
                ? (isTr ? 'Farklı bir terim dene veya aramayı temizle.' : 'Try a different term or clear the search.')
                : (isTr ? 'İlk analizini yükle — dakikalar içinde detaylı bir rapor.' : 'Upload your first script — a detailed report in minutes.')}
            </p>
            {!searchQuery && (
              <button onClick={() => navigate('/upload')} className="btn-accent">
                {isTr ? 'YENİ ANALİZ' : 'NEW ANALYSIS'}
                <span aria-hidden>→</span>
              </button>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default DashboardPage;
